import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

export default function Footer() {
  const footerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.25 }
    );

    if (footerRef.current) {
      observer.observe(footerRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  const services = [
    'Parking Lot Striping',
    'Asphalt Sealcoating',
    'Crack Sealing',
    'Pothole Repair',
    'ADA Compliance'
  ];

  const company = [
    { name: 'Services', path: '/services' },
    { name: 'Our Work', path: '/our-work' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <footer ref={footerRef} className="bg-gray-950 text-gray-300 relative overflow-hidden">
      {/* Subtle skyline pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute bottom-0 left-0 right-0 h-32" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 1200 120\'%3E%3Cpath fill=\'%23ffffff\' d=\'M0,0 L50,80 L100,60 L150,90 L200,50 L250,70 L300,40 L350,85 L400,55 L450,75 L500,45 L550,80 L600,50 L650,70 L700,60 L750,85 L800,55 L850,75 L900,65 L950,80 L1000,50 L1050,70 L1100,60 L1150,85 L1200,55 L1200,120 L0,120 Z\'/%3E%3C/svg%3E")' }}></div>
      </div>

      <div className={`relative z-10 max-w-[1180px] mx-auto px-6 lg:px-8 py-12 lg:py-16 ${isVisible ? 'lg:animate-fade-up' : 'lg:opacity-0'}`}>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10 mb-8 lg:mb-12">
          {/* Brand Column */}
          <div>
            <img
              src="https://static.readdy.ai/image/77d46a0f4a774521f072e7d3cc1d9be9/2562aa2f91a4d69fff4ae7377a088fea.png"
              alt="Sooner Blacktop"
              className="h-12 w-auto mb-4"
            />
            <p className="text-gray-400 mb-6 leading-[1.45]">
              Professional parking lot maintenance serving the Oklahoma City metro area.
            </p>
            <div className="flex items-center space-x-2">
              <input
                type="email"
                placeholder="Your email"
                className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-yellow-400 focus:border-transparent outline-none"
              />
              <button className="w-10 h-10 bg-yellow-400 text-black rounded-lg hover:bg-yellow-500 transition-colors flex items-center justify-center cursor-pointer lg:hover-lift">
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h3 className="text-white font-bold text-lg mb-4">Services</h3>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <Link
                    to="/services"
                    className="text-gray-400 hover:text-yellow-400 transition-colors cursor-pointer"
                  >
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h3 className="text-white font-bold text-lg mb-4">Company</h3>
            <ul className="space-y-3">
              {company.map((item, index) => (
                <li key={index}>
                  <Link
                    to={item.path}
                    className="text-gray-400 hover:text-yellow-400 transition-colors cursor-pointer"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h3 className="text-white font-bold text-lg mb-4">Contact</h3>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:405-367-6799"
                  className="text-white text-xl font-bold hover:text-yellow-400 transition-colors cursor-pointer"
                >
                  (405) 367-6799
                </a>
              </li>
              <li>
                <a
                  href="mailto:Contact@SoonerBlacktop.com"
                  className="text-gray-400 hover:text-yellow-400 transition-colors cursor-pointer"
                >
                  Contact@SoonerBlacktop.com
                </a>
              </li>
              <li className="text-gray-400">
                <strong className="text-white block mb-1">Hours:</strong>
                24/7
              </li>
              <li className="text-gray-400 leading-[1.45]">
                2520 NW 39th St<br />
                Oklahoma City, OK 73112
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 lg:pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4">
            <p className="text-sm text-gray-500">
              © {currentYear} Sooner Blacktop. All rights reserved.
            </p>
            <span className="hidden md:inline text-gray-700">•</span>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-gray-500 hover:text-yellow-400 transition-colors cursor-pointer"
            >
              Website Builder
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
